function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function roundTimeBucket(seconds, precision = 10) {
  return Math.round(seconds * precision) / precision;
}

function normalizeAspectRatio(value, fallback = 16 / 9) {
  const nextValue = Number(value);
  return Number.isFinite(nextValue) && nextValue > 0 ? nextValue : fallback;
}

export function useTimelineThumbnails() {
  const thumbnails = ref([]);
  const isGenerating = ref(false);
  const error = ref(null);
  const frameCapture = useVideoFrameCapture();
  const {sourceAspectRatio} = frameCapture;

  let generationId = 0;

  function buildStripPlan({duration, captureCount, thumbWidthPx, thumbHeightPx, sourceAspectRatio}) {
    const safeDuration = Math.max(0, Number(duration) || 0);
    const safeCaptureCount = clamp(Math.floor(captureCount || 27), 1, 120);
    const safeThumbHeight = Math.max(16, Math.floor(thumbHeightPx || 64));
    const fallbackAspectRatio = normalizeAspectRatio(Number(thumbWidthPx) / safeThumbHeight);
    const safeThumbWidth = Math.max(
      16,
      Math.round(safeThumbHeight * normalizeAspectRatio(sourceAspectRatio, fallbackAspectRatio))
    );

    if (!safeDuration) {
      return [];
    }

    const intervalSec = safeDuration / safeCaptureCount;

    return Array.from({length: safeCaptureCount}, (_, index) => {
      const rawTime = (index + 0.5) * intervalSec;
      const maxTime = Math.max(0, safeDuration - 0.001);
      const timeSec = roundTimeBucket(clamp(rawTime, 0, maxTime));

      return {
        id: `${index}-${timeSec}`,
        index,
        timeSec,
        widthPx: safeThumbWidth,
        heightPx: safeThumbHeight,
        imageSrc: null
      };
    });
  }

  async function generateStrip({src, duration, captureCount = 27, thumbWidthPx = 56, thumbHeightPx = 64}) {
    const currentGenerationId = ++generationId;
    error.value = null;

    if (!src || !duration) {
      thumbnails.value = [];
      isGenerating.value = false;
      frameCapture.cancel();
      return [];
    }

    isGenerating.value = true;

    try {
      const sourceMeta = await frameCapture.loadSource(src);

      if (currentGenerationId !== generationId) {
        return thumbnails.value;
      }

      const stripPlan = buildStripPlan({
        duration,
        captureCount,
        thumbWidthPx,
        thumbHeightPx,
        sourceAspectRatio: sourceMeta?.aspectRatio || sourceAspectRatio.value
      });
      thumbnails.value = stripPlan;

      for (let index = 0; index < stripPlan.length; index++) {
        if (currentGenerationId !== generationId) {
          return thumbnails.value;
        }

        const item = stripPlan[index];
        const imageSrc = await frameCapture.captureAt({
          src,
          timeSec: item.timeSec,
          heightPx: item.heightPx,
          quality: 0.6
        });

        if (currentGenerationId !== generationId) {
          return thumbnails.value;
        }

        thumbnails.value[index] = {
          ...thumbnails.value[index],
          imageSrc
        };
      }
    } catch (err) {
      if (currentGenerationId === generationId) {
        error.value = err;
      }
    } finally {
      if (currentGenerationId === generationId) {
        isGenerating.value = false;
      }
    }

    return thumbnails.value;
  }

  function cancel() {
    generationId++;
    isGenerating.value = false;
    frameCapture.cancel();
  }

  function reset() {
    cancel();
    thumbnails.value = [];
    error.value = null;
  }

  function dispose() {
    reset();
    frameCapture.dispose();
  }

  return {
    thumbnails,
    isGenerating,
    error,
    sourceAspectRatio,
    generateStrip,
    cancel,
    reset,
    dispose
  };
}
